import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Paper,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Warning,
  Error as ErrorIcon,
  Assignment,
  Build,
  CheckCircle,
  Engineering,
  PlayArrow,
  Save,
  Schedule,
  Person,
  Edit,
  Visibility,
  Settings,
  PriorityHigh,
  AccessTime,
  Done,
  DoneAll,
  Pause,
  RestoreFromTrash,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { maintenanceService } from '../../services/maintenanceService';
import EnhancedMaintenanceCompletionForm from './EnhancedMaintenanceCompletionForm';
import { StandardTaskAssignment } from '../../types/maintenance';

// Enhanced interfaces
interface MaintenancePackage {
  id: string;
  task_name: string;
  task_description: string;
  site_name: string;
  site_code: string;
  next_due_date: string;
  days_until_due: number;
  is_overdue: boolean;
  frequency_display: string;
  priority: string;
  priority_display: string;
  status: string;
  status_display: string;
  
  // Assignment tracking
  total_assignments_count: number;
  due_assignments_count: number;
  has_overdue_assignments: boolean;
  overdue_assignments_count: number;
  is_partially_overdue: boolean;
  assignment_completion_status: {
    total_assignments: number;
    completed_assignments: number;
    overdue_assignments: number;
    due_today_assignments: number;
    upcoming_assignments: number;
    completion_percentage: number;
  };
  
  // Detailed assignment data
  task_assignments_detail: StandardTaskAssignment[];
  overdue_assignments_detail: StandardTaskAssignment[];
  
  // Resource information
  estimated_total_duration_hours: number;
  required_crew_size: number;
  
  // Progress tracking
  has_draft?: boolean;
  draft_updated_at?: string;
  in_progress?: boolean;
}

interface TaskDataField {
  id: string;
  field_name: string;
  field_label: string;
  input_type: string;
  is_required: boolean;
  placeholder_text: string;
  help_text: string;
  options: string[];
  unit_of_measure: string;
  min_value: number | null;
  max_value: number | null;
  decimal_places: number;
  section_name: string;
  display_order: number;
  has_options: boolean;
  is_numeric: boolean;
}

interface MaintenancePackageWorkflowProps {
  siteId?: string;
  onPackageComplete?: (packageId: string) => void;
  onAssignmentComplete?: (packageId: string, assignmentId: string) => void;
  showOverdueOnly?: boolean;
  showDraftResumePrompts?: boolean;
}

const MaintenancePackageWorkflow: React.FC<MaintenancePackageWorkflowProps> = ({
  siteId,
  onPackageComplete,
  onAssignmentComplete,
  showOverdueOnly = false,
  showDraftResumePrompts = true,
}) => {
  const [packages, setPackages] = useState<MaintenancePackage[]>([]);
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Completion workflow state
  const [completionFormOpen, setCompletionFormOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<MaintenancePackage | null>(null);
  const [completionMode, setCompletionMode] = useState<'complete' | 'assign' | 'view'>('complete');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [draftData, setDraftData] = useState<any>(null);

  // Progress tracking
  const [packagesInProgress, setPackagesInProgress] = useState<Set<string>>(new Set());

  // Load packages
  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (siteId) params.append('site', siteId);
      if (showOverdueOnly) params.append('has_overdue_assignments', 'true');

      const response = await fetch(`/api/v1/maintenance/tasks/?${params}`);
      const data = await response.json();
      
      // Enhanced data processing
      const processedPackages = (data.results || []).map((pkg: any) => ({
        ...pkg,
        has_draft: false, // Will be loaded separately
        in_progress: packagesInProgress.has(pkg.id),
      }));

      // Load draft information for packages
      for (const pkg of processedPackages) {
        try {
          const draftResponse = await maintenanceService.getCompletionDraft(pkg.id);
          if (draftResponse.draft_data) {
            pkg.has_draft = true;
            pkg.draft_updated_at = draftResponse.last_updated_at;
          }
        } catch (err) {
          // No draft exists, which is fine
        }
      }

      setPackages(processedPackages);
    } catch (err) {
      console.error('Failed to load maintenance packages:', err);
      setError('Failed to load maintenance packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, [siteId, showOverdueOnly]);

  const handleToggleExpand = (packageId: string) => {
    const newExpanded = new Set(expandedPackages);
    if (newExpanded.has(packageId)) {
      newExpanded.delete(packageId);
    } else {
      newExpanded.add(packageId);
    }
    setExpandedPackages(newExpanded);
  };

  const handleStartCompletion = async (pkg: MaintenancePackage) => {
    setSelectedPackage(pkg);
    setCompletionMode('complete');
    
    // Mark as in progress
    setPackagesInProgress(prev => {
      const newSet = new Set(prev);
      newSet.add(pkg.id);
      return newSet;
    });
    
    // Load available technicians and any existing draft
    try {
      const [users, draft] = await Promise.all([
        maintenanceService.getAvailableTechnicians(pkg.id),
        maintenanceService.getCompletionDraft(pkg.id)
      ]);
      
      setAvailableUsers(users);
      setDraftData(draft.draft_data || null);
    } catch (error) {
      console.error('Error loading completion data:', error);
    }
    
    setCompletionFormOpen(true);
  };

  const handleAssignTechnician = async (pkg: MaintenancePackage) => {
    setSelectedPackage(pkg);
    setCompletionMode('assign');
    
    try {
      const users = await maintenanceService.getAvailableTechnicians(pkg.id);
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
    
    setCompletionFormOpen(true);
  };

  const handleCompletionSubmit = async (completionData: any) => {
    if (!selectedPackage) return;
    
    try {
      if (completionMode === 'assign') {
        await maintenanceService.assignTechnician(selectedPackage.id, completionData.work_order);
        alert('Work order created and assigned successfully!');
      } else {
        await maintenanceService.completeTaskComprehensive(selectedPackage.id, completionData);
        alert('Maintenance package completed successfully!');
        onPackageComplete?.(selectedPackage.id);
      }
      
      setCompletionFormOpen(false);
      setSelectedPackage(null);
      setPackagesInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedPackage.id);
        return newSet;
      });
      
      // Reload packages
      loadPackages();
    } catch (error) {
      console.error('Error processing completion:', error);
      alert('Failed to process request. Please try again.');
    }
  };

  const handleSaveDraft = async (draftData: any) => {
    if (!selectedPackage) return;
    
    try {
      await maintenanceService.saveCompletionDraft(selectedPackage.id, draftData);
      alert('Progress saved successfully!');
      
      // Update the package to show it has a draft
      setPackages(prev => prev.map(pkg => 
        pkg.id === selectedPackage.id 
          ? { ...pkg, has_draft: true, draft_updated_at: new Date().toISOString() }
          : pkg
      ));
      
      // Update the current draft data so form stays populated
      setDraftData(draftData);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save progress. Please try again.');
    }
  };

  const handleResumeDraft = async (pkg: MaintenancePackage) => {
    handleStartCompletion(pkg);
  };

  const handleDeleteDraft = async (pkg: MaintenancePackage) => {
    try {
      // Note: You might need to implement this endpoint
      await fetch(`/api/v1/maintenance/tasks/${pkg.id}/delete_completion_draft/`, {
        method: 'DELETE',
      });
      
      setPackages(prev => prev.map(p => 
        p.id === pkg.id 
          ? { ...p, has_draft: false, draft_updated_at: undefined }
          : p
      ));
      
      alert('Draft deleted successfully!');
    } catch (error) {
      console.error('Error deleting draft:', error);
      alert('Failed to delete draft.');
    }
  };

  const getPackagePriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getPackageStatusColor = (pkg: MaintenancePackage) => {
    if (pkg.is_overdue) return 'error';
    if (pkg.has_overdue_assignments) return 'warning';
    if (pkg.days_until_due <= 7) return 'warning';
    return 'success';
  };

  const getCompletionProgressBar = (pkg: MaintenancePackage) => {
    const { completion_percentage } = pkg.assignment_completion_status;
    return (
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress 
          variant="determinate" 
          value={completion_percentage} 
          color={completion_percentage === 100 ? 'success' : 'primary'}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const overduePackages = packages.filter(pkg => pkg.is_overdue || pkg.has_overdue_assignments);
  const upcomingPackages = packages.filter(pkg => !pkg.is_overdue && !pkg.has_overdue_assignments);

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Warning color="error" sx={{ mr: 1 }} />
                <Typography variant="h4" color="error">
                  {overduePackages.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Overdue Packages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule color="warning" sx={{ mr: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {upcomingPackages.filter(pkg => pkg.days_until_due <= 7).length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Due This Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Pause color="info" sx={{ mr: 1 }} />
                <Typography variant="h4" color="info.main">
                  {packages.filter(pkg => pkg.has_draft).length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment color="primary" sx={{ mr: 1 }} />
                <Typography variant="h4" color="primary">
                  {packages.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Packages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Overdue Packages Section */}
      {overduePackages.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Warning color="error" sx={{ mr: 1 }} />
            Overdue Maintenance Packages ({overduePackages.length})
          </Typography>
          
          {overduePackages.map((pkg) => (
            <Card key={pkg.id} sx={{ mb: 2, border: '1px solid', borderColor: 'error.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        {pkg.task_name}
                      </Typography>
                      <Chip 
                        label={pkg.priority_display} 
                        color={getPackagePriorityColor(pkg.priority) as any}
                        size="small" 
                        sx={{ mr: 1 }}
                      />
                      <Chip 
                        label={`${pkg.days_until_due} days overdue`}
                        color="error"
                        size="small"
                      />
                      {pkg.has_draft && (
                        <Tooltip title={`Draft saved ${pkg.draft_updated_at ? dayjs(pkg.draft_updated_at).format('MMM DD, HH:mm') : ''}`}>
                          <Chip 
                            icon={<Save />}
                            label="Draft Available" 
                            color="info"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {pkg.site_name} • {pkg.frequency_display} • {pkg.estimated_total_duration_hours}h estimated
                    </Typography>

                    {/* Progress Bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 80 }}>
                        Progress:
                      </Typography>
                      {getCompletionProgressBar(pkg)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {pkg.assignment_completion_status.completion_percentage}%
                      </Typography>
                    </Box>

                    {/* Assignment Summary */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Chip 
                        icon={<ErrorIcon />}
                        label={`${pkg.overdue_assignments_count} overdue`}
                        color="error"
                        size="small"
                      />
                      <Chip 
                        icon={<Assignment />}
                        label={`${pkg.total_assignments_count} total tasks`}
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        icon={<CheckCircle />}
                        label={`${pkg.assignment_completion_status.completed_assignments} completed`}
                        color="success"
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                    {pkg.has_draft ? (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PlayArrow />}
                          onClick={() => handleResumeDraft(pkg)}
                          size="small"
                        >
                          Resume Work
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<RestoreFromTrash />}
                          onClick={() => handleDeleteDraft(pkg)}
                          size="small"
                        >
                          Delete Draft
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Build />}
                          onClick={() => handleStartCompletion(pkg)}
                          size="small"
                        >
                          Complete Now
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Person />}
                          onClick={() => handleAssignTechnician(pkg)}
                          size="small"
                        >
                          Assign
                        </Button>
                      </>
                    )}
                    
                    <IconButton
                      onClick={() => handleToggleExpand(pkg.id)}
                      size="small"
                    >
                      {expandedPackages.has(pkg.id) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Expanded Details */}
                <Collapse in={expandedPackages.has(pkg.id)}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Individual Task Assignments:
                  </Typography>
                  <List dense>
                    {pkg.task_assignments_detail.map((assignment) => (
                      <ListItem key={assignment.id}>
                        <ListItemIcon>
                          {assignment.is_overdue ? 
                            <ErrorIcon color="error" /> : 
                            <Assignment color="primary" />
                          }
                        </ListItemIcon>
                        <ListItemText
                          primary={assignment.task_name}
                          secondary={`${assignment.equipment_name} • ${assignment.frequency_display} • Due: ${dayjs(assignment.next_due_date).format('MMM DD, YYYY')}`}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={assignment.is_overdue ? `${Math.abs(assignment.days_until_due)} days overdue` : `${assignment.days_until_due} days`}
                            color={assignment.is_overdue ? 'error' : 'default'}
                            size="small"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Upcoming Packages Section */}
      {!showOverdueOnly && upcomingPackages.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Schedule color="primary" sx={{ mr: 1 }} />
            Upcoming Maintenance Packages ({upcomingPackages.length})
          </Typography>
          
          {upcomingPackages.map((pkg) => (
            <Card key={pkg.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        {pkg.task_name}
                      </Typography>
                      <Chip 
                        label={pkg.priority_display} 
                        color={getPackagePriorityColor(pkg.priority) as any}
                        size="small" 
                        sx={{ mr: 1 }}
                      />
                      <Chip 
                        label={`Due in ${pkg.days_until_due} days`}
                        color={pkg.days_until_due <= 7 ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {pkg.site_name} • {pkg.frequency_display} • {pkg.estimated_total_duration_hours}h estimated
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip 
                        icon={<Assignment />}
                        label={`${pkg.total_assignments_count} tasks`}
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        icon={<AccessTime />}
                        label={dayjs(pkg.next_due_date).format('MMM DD, YYYY')}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Build />}
                      onClick={() => handleStartCompletion(pkg)}
                      size="small"
                    >
                      Complete
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<Person />}
                      onClick={() => handleAssignTechnician(pkg)}
                      size="small"
                    >
                      Assign
                    </Button>
                    
                    <IconButton
                      onClick={() => handleToggleExpand(pkg.id)}
                      size="small"
                    >
                      {expandedPackages.has(pkg.id) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedPackages.has(pkg.id)}>
                  <Divider sx={{ my: 2 }} />
                  <List dense>
                    {pkg.task_assignments_detail.map((assignment) => (
                      <ListItem key={assignment.id}>
                        <ListItemIcon>
                          <Assignment color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={assignment.task_name}
                          secondary={`${assignment.equipment_name} • ${assignment.frequency_display}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {packages.length === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No maintenance packages found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showOverdueOnly 
                  ? "All maintenance packages are up to date!" 
                  : "No maintenance packages are scheduled for this site."
                }
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Completion Form */}
      {selectedPackage && (
        <EnhancedMaintenanceCompletionForm
          open={completionFormOpen}
          onClose={() => {
            setCompletionFormOpen(false);
            setSelectedPackage(null);
            setDraftData(null);
            setPackagesInProgress(prev => {
              const newSet = new Set(prev);
              if (selectedPackage) newSet.delete(selectedPackage.id);
              return newSet;
            });
          }}
          onSubmit={handleCompletionSubmit}
          onSaveDraft={handleSaveDraft}
          task={selectedPackage}
          mode={completionMode}
          existingData={draftData}
          availableUsers={availableUsers}
        />
      )}
    </Box>
  );
};

export default MaintenancePackageWorkflow; 